import { Injectable, Inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, Effect, ofType, ROOT_EFFECTS_INIT, createEffect } from '@ngrx/effects';
import { tap, take, mapTo, switchMap, filter, map, delay, catchError, concatMap, withLatestFrom } from 'rxjs/operators';

import { AuthenticationService, AUTH_SERVICE, getLink, SirenResponse, ROUTE_PARAM_REGEX, downloadDocument, SirenHttp } from '@awp/http';
import * as dataApi from '@awp/data/api';

import { ApiResponse, ApiLink } from '@awp/quote/models';
import { ActionTypes, Actions as ActionType, DocumentDownloadSuccessAction, DocumentDownloadFailureAction } from './actions';
import { ThemeService } from '@awp/theme';
import { ContentParser } from '@awp/content';
import { getSirenResponse } from '@awp/data/api';
import { of } from 'rxjs';
import { createErrorModel } from '@awp/core/utils';

@Injectable()
export class AppEffects {

  constructor(
    private actions$: Actions<ActionType>,
    private store: Store<any>,
    private themeService: ThemeService,
    private contentParser: ContentParser,
    private http: SirenHttp,
    @Inject(AUTH_SERVICE) private authService: AuthenticationService) { }

  auth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      concatMap(action => of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(dataApi.getSirenResponse<SirenResponse>(ApiResponse.Authenticate))),
            this.store.pipe(select(dataApi.getSirenResponse<SirenResponse>(ApiResponse.ReAuthenticate)))
          )
        )
      ),
      take(1),
      tap(([action, authResponse, reAuthResponse]) => {
        if (authResponse != null) {
          this.authService.setAuthResponse(authResponse, reAuthResponse);
        }
        this.contentParser.context = this.contentParser.context || { };
        if (authResponse != null) {
          const pdsUrl = this.getPdsUrl(authResponse);

          this.contentParser.context = {
            ...this.contentParser.context,
            url: {
              ...this.contentParser.context.url,
              pdsUrl
            }
          };
        }
      })
    ),
    { dispatch: false }
  );

  reset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.RESET_STATE),
      mapTo(new dataApi.AuthenticateAction())
    )
  );

  themeChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.APP_THEME_CHANGED),
      switchMap(action => this.themeService.getTheme()),
      tap(theme => {
        this.contentParser.context = this.contentParser.context || { };
        this.contentParser.context = {
            ...this.contentParser.context,
            ...theme,
            url: {
              ...this.contentParser.context.url,
              ...theme.url
            }
          };
      })
    ), { dispatch: false }
  );

  sirenAuthResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType<dataApi.SirenResponseAction>(dataApi.ActionTypes.SIREN_RESPONSE),
      filter(action => {
        return action.payload.response != null
          && action.payload.response.rel != null
          && action.payload.response.rel.includes(ApiResponse.Authenticate)
      }),
      map(action => action.payload.response),
      tap(response => {
        this.contentParser.context = this.contentParser.context || { };
        const pdsUrl = this.getPdsUrl(response);
        this.contentParser.context = {
          ...this.contentParser.context,
          url: {
            ...this.contentParser.context.url,
            pdsUrl
          }
        };
      })
    ),
    { dispatch: false }
  );

  downloadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DOCUMENT_DOWNLOAD),
      concatMap(action => of(action).pipe(
          withLatestFrom(this.store.pipe(select(getSirenResponse<SirenResponse>(ApiResponse.Authenticate))))
        )
      ),
      switchMap(([action, authResponse]) => {
        const auth$ = authResponse == null ? this.authService.authenticate() : of(authResponse);
        return auth$
          .pipe(
            delay(500),
            switchMap(auth => {
              this.authService.setAuthResponse(authResponse);
              const link = getLink(ApiLink.PdsDocument, auth);
              return downloadDocument(this.http, 'Pds', link, { route: action.payload }, true)
                .pipe(
                  map(p => new DocumentDownloadSuccessAction()),
                  catchError(err => {
                    console.error('Error download Document');
                    return of(new DocumentDownloadFailureAction(createErrorModel({ err, section: 'Document Download', message: 'Error downloading document' })));
                  })
                );
            })
          );
      })
    )
  );


  private getPdsUrl(response: SirenResponse): string {
    const pdsLink = getLink(ApiLink.PdsDocument, response);
    const url = pdsLink != null ? pdsLink.href : null;

    const pdsUrl = url.replace(ROUTE_PARAM_REGEX, (match, key) => {
      if (key === 'clientname') {
        return '{{context.themeId}}';
      } else {
        return `{${match}}`;
      }
    });

    return pdsUrl;
  }

}
